<template>
  <b-card no-body>
    <b-card-header class="d-flex align-items-center justify-content-between">
      <div>
        <h4 class="mb-0">
          Lagerauslastung Trends
        </h4>
        <small class="text-muted">Auslastung der letzten 12 Monate</small>
      </div>
      <div class="d-flex align-items-center">
        <b-badge 
          :variant="currentUtilization >= 80 ? 'success' : currentUtilization >= 60 ? 'warning' : 'danger'"
          pill
          class="mr-2"
        >
          {{ currentUtilization }}% Auslastung
        </b-badge>
        <b-dropdown
          variant="outline-secondary"
          size="sm"
          no-caret
        >
          <template #button-content>
            <feather-icon
              icon="DownloadIcon"
              size="16"
            />
          </template>
          <b-dropdown-item @click="exportChartData('csv')">
            <feather-icon
              icon="FileTextIcon"
              size="14"
              class="mr-1"
            />
            Als CSV exportieren
          </b-dropdown-item>
          <b-dropdown-item @click="exportChartData('png')">
            <feather-icon
              icon="ImageIcon"
              size="14"
              class="mr-1"
            />
            Als PNG exportieren
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </b-card-header>
    
    <b-card-body class="px-3 py-3">
      <apexchart
        ref="chart"
        type="area"
        height="300"
        :options="chartOptions"
        :series="series"
      />
      
      <!-- Summary Stats -->
      <b-row class="mt-3">
        <b-col
          cols="3"
          class="text-center"
        >
          <h5 class="text-success mb-0">
            {{ averageUtilization }}%
          </h5>
          <small class="text-muted">Ø Auslastung</small>
        </b-col>
        <b-col
          cols="3"
          class="text-center"
        >
          <h5 class="text-primary mb-0">
            {{ peakUtilization }}%
          </h5>
          <small class="text-muted">Höchstwert</small>
        </b-col>
        <b-col
          cols="3"
          class="text-center"
        >
          <h5 class="text-info mb-0">
            {{ trendDirection }}
          </h5>
          <small class="text-muted">Trend</small>
        </b-col>
        <b-col
          cols="3"
          class="text-center"
        >
          <h5 class="text-dark mb-0">
            {{ totalStorages }}
          </h5>
          <small class="text-muted">Gesamt Lager</small>
        </b-col>
      </b-row>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardHeader, BCardBody, BRow, BCol, BBadge, BDropdown, BDropdownItem } from 'bootstrap-vue'
import ApexCharts from 'vue-apexcharts'
import { $themeColors } from '@themeConfig'
import businessMetrics from '@/helper/businessMetrics'

export default {
  name: 'StorageUtilizationChart',
  components: {
    BCard,
    BCardHeader, 
    BCardBody,
    BRow,
    BCol,
    BBadge,
    BDropdown,
    BDropdownItem,
    apexchart: ApexCharts,
  },
  setup() {
    const { getStorageUtilizationTrends, getCountOfAllStorages } = businessMetrics()
    return {
      getStorageUtilizationTrends,
      getCountOfAllStorages,
    }
  },
  data() {
    return {
      series: [],
      chartOptions: {
        chart: {
          type: 'area',
          height: 300,
          toolbar: { 
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true,
            },
          },
          zoom: { enabled: true },
          fontFamily: 'inherit',
        },
        colors: [$themeColors.primary],
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'smooth',
          width: 3,
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.1,
            stops: [0, 90, 100],
          },
        },
        grid: {
          borderColor: '#e4e6e8',
          strokeDashArray: 3,
          padding: {
            top: -20,
            bottom: 5,
            left: 20,
          },
        },
        xaxis: {
          categories: [],
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            style: {
              colors: '#6c757d',
              fontSize: '12px',
            },
          },
        },
        yaxis: {
          min: 0,
          max: 100,
          labels: {
            style: {
              colors: '#6c757d',
              fontSize: '12px',
            },
            formatter: val => `${val}%`,
          },
        },
        tooltip: {
          y: {
            formatter: (val, { dataPointIndex }) => {
              const data = this.utilizationData[this.monthLabels[dataPointIndex]]
              return `
                <div>
                  <strong>${val}% Auslastung</strong><br/>
                  Vermietet: ${data?.occupied || 0}<br/>
                  Verfügbar: ${data?.available || 0}<br/>
                  Gesamt: ${data?.total || 0}
                </div>
              `
            },
          },
        },
        legend: { show: false },
      },
      utilizationData: {},
      monthLabels: [],
    }
  },
  computed: {
    currentUtilization() {
      if (this.monthLabels.length === 0) return 0
      const lastMonth = this.monthLabels[this.monthLabels.length - 1]
      return this.utilizationData[lastMonth]?.percentage || 0
    },
    averageUtilization() {
      if (this.monthLabels.length === 0) return 0
      const percentages = this.monthLabels.map(month => this.utilizationData[month]?.percentage || 0)
      return Math.round(percentages.reduce((sum, val) => sum + val, 0) / percentages.length)
    },
    peakUtilization() {
      if (this.monthLabels.length === 0) return 0
      const percentages = this.monthLabels.map(month => this.utilizationData[month]?.percentage || 0)
      return Math.max(...percentages)
    },
    trendDirection() {
      if (this.monthLabels.length < 2) return '→'
      const firstHalf = this.monthLabels.slice(0, 6).map(month => this.utilizationData[month]?.percentage || 0)
      const secondHalf = this.monthLabels.slice(6).map(month => this.utilizationData[month]?.percentage || 0)
      
      const firstAvg = firstHalf.reduce((sum, val) => sum + val, 0) / firstHalf.length
      const secondAvg = secondHalf.reduce((sum, val) => sum + val, 0) / secondHalf.length
      
      if (secondAvg > firstAvg + 2) return '↗'
      if (secondAvg < firstAvg - 2) return '↘'
      return '→'
    },
    totalStorages() {
      return this.getCountOfAllStorages()
    },
  },
  mounted() {
    this.loadChartData()
  },
  methods: {
    exportChartData(format) {
      if (format === 'csv') {
        this.exportToCSV()
      } else if (format === 'png') {
        this.exportToPNG()
      }
    },
    exportToCSV() {
      const headers = ['Monat', 'Auslastung (%)', 'Vermietet', 'Verfügbar', 'Gesamt']
      const rows = this.monthLabels.map(month => {
        const data = this.utilizationData[month]
        return [
          month,
          data?.percentage || 0,
          data?.occupied || 0,
          data?.available || 0,
          data?.total || 0,
        ]
      })
      
      let csvContent = `${headers.join(',')}\n`
      rows.forEach(row => {
        csvContent += `${row.join(',')}\n`
      })
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', 'lagerauslastung-trends.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    exportToPNG() {
      this.$refs.chart.dataURI().then(uri => {
        const link = document.createElement('a')
        link.href = uri
        link.download = 'lagerauslastung-trends.png'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
    },
    loadChartData() {
      this.utilizationData = this.getStorageUtilizationTrends()
      this.monthLabels = Object.keys(this.utilizationData)
      
      if (this.monthLabels.length === 0) {
        // Show empty state if no data
        this.series = [{ name: 'Auslastung %', data: [] }]
        return
      }

      // Prepare chart data
      const percentageData = this.monthLabels.map(month => this.utilizationData[month].percentage)
      
      this.series = [
        {
          name: 'Auslastung %',
          data: percentageData,
        },
      ]
      
      this.chartOptions = {
        ...this.chartOptions,
        xaxis: {
          ...this.chartOptions.xaxis,
          categories: this.monthLabels,
        },
      }
    },
  },
}
</script>

<style scoped>
.chart-container {
  position: relative;
}
</style>
